import {Button, Divider, Group} from "@mantine/core";
import {useCallback} from "react";
import {useTranslation} from "react-i18next";
import {useForm} from "@mantine/form";
import {DateInput} from "../../date/DateInput/DateInput";
import {isBefore} from "@ots-frontend/ots-data"
import {
  PipelineFlowSearchFormModal,
  PipelineFlowSearchFormOptions,
  PipelineFlowSearchFormValues
} from "../../form/PipelineFlowSearchForm/PipelineFlowSearchForm";
import {useDisclosure} from "@mantine/hooks";
import {DefaultTFuncReturn} from "i18next";

interface PipelineFlowSearchToolbarProps {
  options?: PipelineFlowSearchFormOptions;
  defaultValues?: Partial<PipelineFlowSearchFormValues>;
  onSubmit?: (values: PipelineFlowSearchFormValues) => void;
  submitting?: boolean;
}

export const PipelineFlowSearchToolbar = (props: PipelineFlowSearchToolbarProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting
  } = props;

  const {t} = useTranslation(["common"]);

  const [advancedOpen, advancedHandler] = useDisclosure(false)

  const form = useForm({
    initialValues: {
      fromDate: defaultValues?.fromDate,
      toDate: defaultValues?.toDate,
    },
    validate: {
      toDate: (value, values) => {
        let error: string | undefined | DefaultTFuncReturn = undefined
        if (value && values.fromDate && isBefore(value, values.fromDate)) {
          error = t<string>("common.form.validation.todatebeforefrom", "The to date can't be before the from date")
        }
        return error
      }
    }
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        ...defaultValues,
        fromDate: values.fromDate!,
        toDate: values.toDate,
      })
    }
  }, [form, defaultValues, onSubmit])

  const onAdvancedSubmit = useCallback((values: PipelineFlowSearchFormValues) => {
    if (onSubmit) {
      onSubmit(values)
    }

    form.setValues({
      fromDate: values.fromDate,
      toDate: values.toDate
    });

    advancedHandler.close()
  }, [form, onSubmit, advancedHandler])

  return (
    <Group position="apart" p="xs">
      <Group position="center">

      </Group>
      <Group position="center">
        <form onSubmit={form.onSubmit(onFormSubmit)}>
          <Group my="xs" align="flex-end">
            <DateInput
              valueFormat="MM/DD/YYYY HH:mm"
              label={t("common.ui.toolbar.fromtodate.form.field.fromdate", "From Date")}
              placeholder={t<string>("common.ui.toolbar.fromtodate.form.field.fromdate", "From Date")}
              clearable
              withAsterisk
              popoverProps={{
                withinPortal: true
              }}
              styles={{wrapper: {width: "200px"}}}
              required
              {...form.getInputProps("fromDate")} />
            <DateInput
              miw={100}
              valueFormat="MM/DD/YYYY HH:mm"
              label={t("common.ui.toolbar.fromtodate.form.field.todate", "To Date")}
              placeholder={t<string>("common.ui.toolbar.fromtodate.form.field.todate", "To Date")}
              clearable
              popoverProps={{
                withinPortal: true
              }}
              styles={{wrapper: {width: "200px"}}}
              {...form.getInputProps("toDate")} />
            <Button type="submit" loading={submitting} variant="outline">
              {t("common.action.submit", {ns: "common"})}
            </Button>
            <Divider orientation="vertical"/>
            <Button
              variant="subtle"
              onClick={() => advancedHandler.open()}>
              Advanced
            </Button>
          </Group>
        </form>
      </Group>
      <Group position="center">

      </Group>

      <PipelineFlowSearchFormModal
        title={t<string>("common.shared.form.advanced", "Advanced")}
        opened={advancedOpen}
        onClose={advancedHandler.close}
        onSubmit={onAdvancedSubmit}
        submitting={submitting}
        options={options}
        defaultValues={defaultValues}/>

    </Group>
  );
};
